/**
 * Whole-model tower skins (TowerKit.model, big kits): the kit's own `tower_<id>_t<n>__<model>.glb` (and
 * `tower_<id>_t4_<branch>__<model>` for a tier-IV branch) replace the stock model wherever the manifest ships them,
 * like the fortress skins' `base_t<n>__<model>`. Pure: names in, names out (`has` says whether a name is available —
 * loaded for drawing, shipped for preloading), so the resolution is unit-tested without a library.
 */

/** A tier-IV branch's own model name (`tower_<id>_t4_<branch>`). */
export function tierFourName(archId: string, branch: string): string { return `tower_${archId}_t4_${branch}`; }

/** A model name in a skin's own model (`tower_archer_t2` + `ivory` → `tower_archer_t2__ivory`). */
export function skinnedName(name: string, model: string): string { return `${name}__${model}`; }

/**
 * The model a tower is drawn with, best first: the branch's own model in the skin's model, the branch's own model, the
 * tier's model in the skin's model, the tier's stock model (tier IV without its own model falls back to tier 3, which
 * views/towerModel.ts then dresses in gold).
 */
export function towerModelName(archId: string, tier: number, branch: string | null, model: string | null, has: (name: string) => boolean): string {
  if (tier >= 4 && branch !== null) {
    const own = tierFourName(archId, branch);
    if (model && has(skinnedName(own, model))) return skinnedName(own, model);
    if (has(own)) return own;
  }
  const stock = `tower_${archId}_t${Math.max(1, Math.min(3, tier))}`;
  return model && has(skinnedName(stock, model)) ? skinnedName(stock, model) : stock;
}

/** Whether a model name is a skin's own tower model (`…__<model>`), and which skin model. */
export function skinModelOf(name: string): string | null { return /^tower_.+__([a-z0-9_]+)$/.exec(name)?.[1] ?? null; }

/**
 * The skin models a screen should preload for the worn tower skins: for each tower worn in a kit with a `model`, its
 * own model at each of `tiers`, and (`branches`) at every tier-IV branch the manifest ships — only those that ship.
 * `modelOf` maps a worn skin id to its kit's model (or null).
 */
export function towerSkinModels(
  worn: ReadonlyMap<string, string>, modelOf: (skin: string) => string | null, tiers: readonly number[], available: ReadonlySet<string>, branches: boolean,
): string[] {
  const out: string[] = [];
  for (const [tower, skin] of worn) {
    const model = modelOf(skin);
    if (!model) continue;
    for (const t of tiers) out.push(skinnedName(`tower_${tower}_t${t}`, model));
    if (branches) for (const n of available) if (n.startsWith(`tower_${tower}_t4_`) && !n.includes("__") && !n.endsWith("_lod1")) out.push(skinnedName(n, model));
  }
  return out.filter((n) => available.has(n));
}
